import { useState, useCallback } from 'react';
import {
  downloadEstimatorReport,
  openEstimatorReportPdf,
} from '../api/commissionReports';

interface EstimatorReportFilters {
  startDate?: string;
  endDate?: string;
}

interface UseEstimatorReportReturn {
  downloadPdf: (filters?: EstimatorReportFilters) => Promise<void>;
  downloadExcel: (filters?: EstimatorReportFilters) => Promise<void>;
  openPdf: (filters?: EstimatorReportFilters) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export function useEstimatorReport(userId: number | null): UseEstimatorReportReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const downloadPdf = useCallback(
    async (filters?: EstimatorReportFilters) => {
      if (!userId) return;
      setIsLoading(true);
      setError(null);
      try {
        await downloadEstimatorReport(userId, 'pdf', filters);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to download report');
      } finally {
        setIsLoading(false);
      }
    },
    [userId]
  );

  const downloadExcel = useCallback(
    async (filters?: EstimatorReportFilters) => {
      if (!userId) return;
      setIsLoading(true);
      setError(null);
      try {
        await downloadEstimatorReport(userId, 'xlsx', filters);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to download report');
      } finally {
        setIsLoading(false);
      }
    },
    [userId]
  );

  const openPdf = useCallback(
    async (filters?: EstimatorReportFilters) => {
      if (!userId) return;
      setIsLoading(true);
      setError(null);
      try {
        await openEstimatorReportPdf(userId, filters);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to open report');
      } finally {
        setIsLoading(false);
      }
    },
    [userId]
  );

  return { downloadPdf, downloadExcel, openPdf, isLoading, error };
}
